import { Sun, Moon, Quote, Laugh, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';
import { motion } from 'framer-motion';

const DailyHighlight = () => {
  const { t } = useLanguage();
  const hour = new Date().getHours();
  
  // Determine the current daily focus
  const getDailyFocus = () => {
    if (hour >= 6 && hour < 9) {
      return {
        path: '/room/morning-ritual',
        title: t('room_morning-ritual') || 'Morning Ritual',
        subtitle: t('available_now') || 'Available now',
        icon: Sun,
        gradient: 'from-amber-500/20 via-orange-500/10 to-transparent',
        borderColor: 'border-amber-500/30 hover:border-amber-400/50',
        iconColor: 'text-amber-400',
        available: true,
      };
    } else if (hour >= 20 && hour <= 23) {
      return {
        path: '/room/evening-ritual',
        title: t('room_evening-ritual') || 'Evening Ritual',
        subtitle: t('available_now') || 'Available now',
        icon: Moon,
        gradient: 'from-indigo-500/20 via-purple-500/10 to-transparent',
        borderColor: 'border-indigo-500/30 hover:border-indigo-400/50',
        iconColor: 'text-indigo-400',
        available: true,
      };
    } else {
      // Default to daily quote/joke
      const isEven = new Date().getDate() % 2 === 0;
      return {
        path: isEven ? '/room/daily-quote' : '/room/daily-joke',
        title: isEven ? (t('room_daily-quote') || 'Daily Quote') : (t('room_daily-joke') || 'Daily Joke'),
        subtitle: t('todays_room') || "Today's room",
        icon: isEven ? Quote : Laugh,
        gradient: 'from-blue-500/20 via-cyan-500/10 to-transparent',
        borderColor: 'border-blue-500/30 hover:border-blue-400/50',
        iconColor: 'text-blue-400',
        available: true,
      };
    }
  };

  const focus = getDailyFocus();
  const Icon = focus.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      <Link
        to={focus.path}
        className={`block relative overflow-hidden border-2 ${focus.borderColor} bg-gradient-to-br ${focus.gradient} rounded-xl p-6 transition-all duration-300 group`}
      >
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-radial from-white/5 to-transparent rounded-full -translate-y-1/2 translate-x-1/2" />
        
        <div className="relative flex items-center gap-4">
          <div className={`p-3 rounded-xl bg-background/50 ${focus.iconColor}`}>
            <Icon className="w-6 h-6" />
          </div>
          
          <div className="flex-1">
            <p className="text-xs text-muted-foreground/50 uppercase tracking-wider mb-1 flex items-center gap-2">
              <Calendar className="w-3 h-3" />
              {focus.subtitle}
            </p>
            <h3 className="text-lg font-light text-foreground group-hover:text-foreground/90 transition-colors">
              {focus.title}
            </h3>
          </div>
          
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <span className="text-xs text-muted-foreground/50">Enter →</span>
          </div>
        </div>

        {focus.available && (
          <div className="absolute top-3 right-3">
            <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              {t('live') || 'Live'}
            </span>
          </div>
        )}
      </Link>
    </motion.div>
  );
};

export default DailyHighlight;
